import { useGetExternalIntegrationQuery, useUserOperationsQuery } from '@Queries/index';
import { brandPrimary } from '@Styles/colors';
import { baseStyleSheet } from '@Styles/styles';
import * as Api from '@ViewModels';
import { Badge } from '@WebComponents/dataBoards/Badge';
import {
	DataGrid,
	DataGridColumn,
	DataGridColumns,
	DataGridRow,
	DataGridRowItem,
	DataGridRows,
} from '@WebComponents/DataGrid';
import { LoadingSpinner } from '@WebComponents/LoadingSpinner';
import { RefreshIcon } from '@WebComponents/svgs/icons/RefreshIcon';
import { WritebackIcon } from '@WebComponents/svgs/icons/WritebackIcon';
import { css } from 'aphrodite';
import moment from 'moment';
import { getDisplayName } from '../../../../../../../models/NameUtils';
import { styleSheet } from '../../styles';

export const ConnectionsView = ({
	integrationProvider,
	impersonationContext,
}: {
	integrationProvider: Api.IntegrationProvider;
	impersonationContext: Api.IImpersonationContext;
}) => {
	const userOperationsQuery = useUserOperationsQuery({
		accountId: impersonationContext?.account?.id,
	});
	const integrationQuery = useGetExternalIntegrationQuery({
		impersonationContext,
		provider: integrationProvider,
	});

	const isLoading = userOperationsQuery.isPending || integrationQuery.isPending;
	if (isLoading) return <LoadingSpinner type='large' />;

	const accountUsers = (userOperationsQuery.data?.values ?? []) as Api.IUserWithStats[];
	const integration = integrationQuery.data ?? null;
	const userConnections = integration?.configuration?.userConnections ?? [];

	return (
		<div className={css(styleSheet.container)}>
			<DataGrid styles={[styleSheet.tableOverrides]}>
				<DataGridColumns>
					<DataGridColumn sticky>Name</DataGridColumn>
					<DataGridColumn sticky>Status</DataGridColumn>
					<DataGridColumn sticky>Account Syncing</DataGridColumn>
					<DataGridColumn sticky>Writebacks</DataGridColumn>
					<DataGridColumn sticky>Last Successful Connection</DataGridColumn>
				</DataGridColumns>

				<DataGridRows>
					{!integrationQuery.isPending &&
						userConnections.map(userConnection => {
							const levitateUser = accountUsers.find(user => user.id === userConnection.userId);
							if (!levitateUser) return null;

							return (
								<DataGridRow key={`row-${levitateUser.id}`}>
									<DataGridRowItem>
										<h6 className={css(baseStyleSheet.truncateText, baseStyleSheet.m0)}>
											{getDisplayName(levitateUser)}
										</h6>
									</DataGridRowItem>

									<DataGridRowItem>
										<Badge
											text={userConnection.isConnected ? 'Connected' : 'Not Connected'}
											foregroundColor={userConnection.isConnected ? '#4CAF50' : '#F44336'}
										/>
									</DataGridRowItem>

									<DataGridRowItem>
										{integration.configuration.syncUserId &&
										userConnection?.userId === integration.configuration.syncUserId ? (
											<span className={css(styleSheet.statusIndicator)}>
												<span>
													<RefreshIcon width={20} height={20} hoverColor={brandPrimary} />
												</span>
												Sync Enabled
											</span>
										) : (
											<div />
										)}
									</DataGridRowItem>

									<DataGridRowItem>
										{userConnection.isConnected ? (
											<span className={css(styleSheet.statusIndicator)}>
												<span>
													<WritebackIcon className={css(styleSheet.statusIndicatorActive)} />
												</span>
												Writebacks Enabled
											</span>
										) : (
											<div />
										)}
									</DataGridRowItem>

									<DataGridRowItem>
										{userConnection.lastUsedDate ? moment(userConnection.lastUsedDate).format('MMM DD, hh:mm a') : '-'}
									</DataGridRowItem>
								</DataGridRow>
							);
						})}

					{integrationQuery.isPending ? (
						<DataGridRow>
							<DataGridRowItem fullColSpan>
								<LoadingSpinner type='large' />
							</DataGridRowItem>
						</DataGridRow>
					) : null}
				</DataGridRows>
			</DataGrid>
		</div>
	);
};
